/**
 * Utility for safer logging that helps prevent exposing sensitive information
 * in production environments.
 */

// Check if we're in development mode
const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development';

/**
 * Safely logs messages only in development environment
 * @param {string} message - The message to log
 * @param {any} data - Optional data to log
 */
export const devLog = (message, data) => {
  if (isDev) {
    if (data !== undefined) {
      console.log(message, data);
    } else {
      console.log(message);
    }
  }
};

/**
 * Logs messages in all environments, but sanitizes data in production
 * to prevent exposing sensitive information
 * @param {string} message - The message to log
 * @param {any} data - Data to log (will be sanitized in production)
 * @param {Array<string>} sensitiveKeys - Keys to sanitize in production
 */
export const safeLog = (message, data, sensitiveKeys = []) => {
  if (!data) {
    console.log(message);
    return;
  }

  if (isDev) {
    console.log(message, data);
  } else {
    // In production, sanitize sensitive data
    if (typeof data === 'object' && data !== null) {
      const sanitizedData = { ...data };
      
      // Add common sensitive keys if not explicitly provided
      const keysToSanitize = [
        ...sensitiveKeys,
        'key', 'apiKey', 'api_key', 'token', 'secret', 'password', 
        'credentials', 'auth'
      ];
      
      // Sanitize any keys that might contain sensitive information
      for (const key in sanitizedData) {
        if (keysToSanitize.some(k => key.toLowerCase().includes(k.toLowerCase()))) {
          sanitizedData[key] = '[REDACTED]';
        }
      }
      
      console.log(message, sanitizedData);
    } else {
      console.log(message, data);
    }
  }
};

/**
 * Always logs errors in all environments but sanitizes sensitive information
 * @param {string} message - Error message prefix
 * @param {Error|any} error - The error to log
 */
export const errorLog = (message, error) => {
  if (isDev) {
    console.error(message, error);
  } else {
    // In production, create a sanitized version without stack traces
    // and potential sensitive information
    let sanitizedError = error;
    
    if (error && typeof error === 'object') {
      if (error instanceof Error) {
        sanitizedError = {
          name: error.name,
          message: error.message,
          // Omit stack trace in production
        };
      } else {
        // For other objects, create a copy and sanitize sensitive fields
        sanitizedError = { ...error };
        for (const key in sanitizedError) {
          if (['key', 'apiKey', 'api_key', 'token', 'secret', 'password', 'credentials'].some(
              k => key.toLowerCase().includes(k.toLowerCase()))) {
            sanitizedError[key] = '[REDACTED]';
          }
        }
      }
    }
    
    console.error(message, sanitizedError);
  }
};

export default {
  devLog,
  safeLog,
  errorLog
}; 